import "./App.css";
import { Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import React, { useEffect, lazy, Suspense } from 'react';
import ScrollToTop from "./ScrollToTop";
import Navbar from "./components/Navbar";
import { AuthProvider, useAuth } from "./context/AuthContext";

/* ── Only the home page ships in the first download. ──
   Every other page is its own chunk, fetched when someone opens it
   (and the likely next pages are prefetched once the home page is idle). */
import Home2 from "./pages/home2.jsx";

const loadSearchForm = () => import("./pages/SearchForm");
const loadCuratedTripsIndex = () => import("./pages/CuratedTripsIndex");
const loadCuratedTripPage = () => import("./pages/CuratedTripPage");
const loadChat = () => import("./pages/chat");

const SearchForm = lazy(loadSearchForm);
const CuratedTripsIndex = lazy(loadCuratedTripsIndex);
const CuratedTripPage = lazy(loadCuratedTripPage);
const Chat = lazy(loadChat);

const AuthPage = lazy(() => import("./pages/AuthPage"));
const Contact = lazy(() => import("./pages/contact"));
const TypewriterComponent = lazy(() => import("typewriter-effect"));
const AIShowcase = lazy(() => import("./pages/AIShowcase"));
const BookFlight = lazy(() => import("./pages/BookFlight"));
const Test = lazy(() => import("./pages/test"));
const TestPayment = lazy(() => import("./components/testBooking/testPayment"));
const FlightBooking = lazy(() => import("./pages/FligthBooking"));
const BusBooking = lazy(() => import("./pages/BusBooking"));
const TestComponents = lazy(() => import("./pages/TestComponents"));
const BookingHistory = lazy(() => import("./pages/BookingHistory"));
const BookingDetail = lazy(() => import("./pages/BookingDetail"));
const SharedMapPage = lazy(() => import("./pages/SharedMapPage"));
const TripPlanner = lazy(() => import("./TripPlanner"));
const TripShare = lazy(() => import("./TripShare"));
const HotelBookingPage = lazy(() => import("./pages/HotelBookingPage"));
const HowToReach = lazy(() => import("./pages/HowToReach"));
const HowToReachIndex = lazy(() => import("./pages/HowToReachIndex"));
const ShortLinkResolver = lazy(() => import("./pages/ShortLinkResolver"));
const BookingHandoff = lazy(() => import("./pages/BookingHandoff"));
const BusBookingPage = lazy(() => import("./pages/BusBookingPage"));
const BusBookingConfirmationPage = lazy(() => import("./pages/BusBookingConfirmationPage"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const CjpProtestDelhi = lazy(() => import("./pages/CjpProtestDelhi"));
const CreatorStorefront = lazy(() => import("./pages/CreatorStorefront"));
const CreatorTripPage = lazy(() => import("./pages/CreatorTripPage"));
const TaaiLanding = lazy(() => import("./pages/TaaiLanding"));
const DmcTripPage = lazy(() => import("./pages/DmcTripPage"));
const DmcStorefront = lazy(() => import("./pages/DmcStorefront"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const PartnerJoin = lazy(() => import("./pages/PartnerJoin"));
// Hornbill Festival 2026: guide, packages from local operators (by price band), package pages; operator claim links
const HornbillFestival = lazy(() => import("./pages/HornbillFestival"));
const HornbillPackages = lazy(() => import("./pages/HornbillPackages"));
const HornbillPackage = lazy(() => import("./pages/HornbillPackage"));
const ClaimListing = lazy(() => import("./pages/ClaimListing"));
const ListYourTrip = lazy(() => import("./pages/ListYourTrip"));
const DmcPreviewPage = lazy(() => import("./pages/DmcTripPage").then((m) => ({ default: m.DmcPreviewPage })));
const DmcIndex = lazy(() => import("./pages/DmcIndex"));
const FlightSeoPage = lazy(() => import("./pages/TravelIntentPages").then(m => ({ default: m.FlightSeoPage })));
const BusSeoPage = lazy(() => import("./pages/TravelIntentPages").then(m => ({ default: m.BusSeoPage })));
const CompareSeoPage = lazy(() => import("./pages/TravelIntentPages").then(m => ({ default: m.CompareSeoPage })));

/* Not needed for first paint: login modal (opens on demand) and the lead-bot mascot */
const LoginModal = lazy(() => import("./components/LoginModal.jsx"));
const LeadBot = lazy(() => import("./components/lead-bot").then(m => ({ default: m.LeadBot })));

/* Routy (lead bot) waits until the page has settled: first tap/scroll/keypress, or
   ~3.5s after load on the home page (1.5s elsewhere), whichever comes first.
   Its own nudges fire after 20s+ anyway, so nothing visible is lost. */
const useDeferredMount = (pathname) => {
  const [ready, setReady] = React.useState(false);
  useEffect(() => {
    if (ready) return undefined;
    const go = () => setReady(true);
    const delay = pathname === "/" ? 3500 : 1500;
    let t = null;
    const arm = () => { t = setTimeout(go, delay); };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    const evs = ["pointerdown", "keydown", "scroll", "touchstart"];
    evs.forEach(e => window.addEventListener(e, go, { once: true, passive: true }));
    return () => {
      clearTimeout(t);
      window.removeEventListener("load", arm);
      evs.forEach(e => window.removeEventListener(e, go));
    };
  }, [ready, pathname]);
  return ready;
};

/* Shown for the split second a page chunk is downloading */
const PageLoader = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{
      width: 32, height: 32, borderRadius: "50%",
      border: "3px solid #FFE4D6", borderTopColor: "#FF4E01",
      animation: "rc-spin .8s linear infinite",
    }} />
    <style>{`@keyframes rc-spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

/* After the home page has painted and gone idle, warm up the pages people
   open next, so tapping Search / a trip feels instant. Skipped on Save-Data / 2G. */
const usePrefetchNextPages = () => {
  useEffect(() => {
    const c = navigator.connection;
    if (c && (c.saveData || /2g/.test(c.effectiveType || ""))) return;
    const run = () => { loadSearchForm(); loadCuratedTripPage(); loadCuratedTripsIndex(); loadChat(); };
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2500));
    const t = setTimeout(() => idle(run, { timeout: 5000 }), 1500);
    return () => clearTimeout(t);
  }, []);
};

const Layout = ({ children }) => {
  const location = useLocation();
  // Exclude Navbar from these paths
  const excludeNavbarPaths = ["/auth", "/search-form", "/r", "/shared-route", "/trip-planner", "/trip-share", "/open-booking", "/c", "/taai"];
  const shouldShowNavbar = !excludeNavbarPaths.some(path =>
    location.pathname === path ||
    location.pathname.startsWith(path + "/") ||
    (path === "/r" && location.pathname.startsWith("/r"))
  );

  const { isModalOpen } = useAuth();
  const botReady = useDeferredMount(location.pathname);

  return (
    <>
      <ScrollToTop />
      {shouldShowNavbar && <Navbar />}
      {children}
      <Suspense fallback={null}>
        {/* Only fetched when someone actually opens sign-in (pulls in the phone-input library) */}
        {isModalOpen && <LoginModal />}
        {/* Lead capture mascot: shows itself on "/", /search-form, booking pages and /trips/:slug */}
        {botReady && <LeadBot />}
      </Suspense>
    </>
  );
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  usePrefetchNextPages();

  // ESC closes auth route
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && location.pathname === "/auth") {
        navigate("/", { replace: true });
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!window.gtagScriptLoaded) {
      // dataLayer/gtag are set up immediately (events queue up), but the
      // ~100KB GTM script itself is fetched only after the page has loaded.
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-LYGBCY5BKH";
      const inject = () => document.head.appendChild(script);
      if (document.readyState === "complete") setTimeout(inject, 1200);
      else window.addEventListener("load", () => setTimeout(inject, 1200), { once: true });
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", "G-LYGBCY5BKH");
      window.gtagScriptLoaded = true;
    }
  }, []);

  useEffect(() => {
    if (window.gtag) {
      window.gtag("config", "G-LYGBCY5BKH", {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return (
    <AuthProvider>
      <Layout>
        <ToastContainer />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home2 />} />
          <Route path="/plan-a-trip" element={<Home2 initialTab="plan" />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/typetest" element={<TypewriterComponent />} />
          <Route path="/ai-test" element={<AIShowcase />} />
          <Route path="/search-form/:status?" element={<SearchForm />} />
          <Route path="/home.html" element={<Navigate to="/" />} />
          <Route path="/home" element={<Navigate to="/" />} />

          <Route path="/book-flight" element={<BookFlight />} />
          <Route path="/booking-flight" element={<FlightBooking />} />
          <Route path="/booking-history" element={<BookingHistory />} />
          <Route path="/booking-detail" element={<BookingDetail />} />

          <Route path="/test" element={<Test />} />
          <Route path="/test/components" element={<TestComponents />} />
          <Route path="/test-payment" element={<TestPayment />} />
          <Route path="/book-bus/status" element={<BusBooking />} />

          {/* Shared Map Dynamic Route */}
          <Route path="/shared-route" element={<SharedMapPage />} />

          {/* Short shared-route links — resolved in-app (no CloudFront redirect,
              so long rdata URLs can never 414). /b/ booking links stay on the
              CloudFront redirect path untouched. */}
          <Route path="/r/:code" element={<ShortLinkResolver />} />

          {/* SEO corridor pages ("How to reach X from Y") */}
          <Route path="/how-to-reach/:slug" element={<HowToReach />} />
          <Route path="/how-to-reach" element={<HowToReachIndex />} />

          {/* AI Trip Planner */}
          <Route path="/trip-planner" element={<TripPlanner />} />
          <Route path="/trip-planner/hotel/:hotelCode" element={<HotelBookingPage />} />
          <Route path="/trip-share" element={<TripShare />} />
          <Route path="/book-bus" element={<BusBookingPage />} />
          {/* New-tab booking landing: picks up booking state handed over by the results tab */}
          <Route path="/open-booking" element={<BookingHandoff />} />
          <Route path="/book-bus/confirmation" element={<BusBookingConfirmationPage />} />
          <Route path="/partner/:agentId" element={<PartnerDashboard />} />
          <Route path="/partner-signup" element={<PartnerJoin />} />
          <Route path="/hornbill-festival" element={<HornbillFestival />} />
          <Route path="/hornbill-festival/packages" element={<HornbillPackages />} />
          <Route path="/hornbill-festival/packages/:band" element={<HornbillPackages />} />
          <Route path="/hornbill-festival/package/:slug" element={<HornbillPackage />} />
          <Route path="/claim/:token" element={<ClaimListing />} />
          {/* "List your trip": PDF -> private trip page (/p/<token>), claimed from the emailed link */}
          <Route path="/list-your-trip" element={<ListYourTrip />} />
          <Route path="/p/:token" element={<DmcPreviewPage />} />
          <Route path="/cjp-protest-2-0-delhi" element={<CjpProtestDelhi />} />

          {/* Creator Trips (flag: FEATURES.ENABLE_CREATOR_TRIPS, checked inside the pages) */}
          <Route path="/c/:handle" element={<CreatorStorefront />} />
          <Route path="/c/:handle/:slug" element={<CreatorTripPage />} />

          {/* Curated, AI-editable trips (flag: FEATURES.ENABLE_CURATED_TRIPS, checked inside the pages) */}
          <Route path="/trips" element={<CuratedTripsIndex />} />
          <Route path="/trips/:slug" element={<CuratedTripPage />} />

          {/* DMC partner trips: package (hotel + cab) by the partner, routes by RouteChef (flag: FEATURES.ENABLE_DMC_TRIPS) */}
          <Route path="/dmc" element={<DmcIndex />} />
          <Route path="/dmc/:partnerSlug" element={<DmcStorefront />} />
          <Route path="/dmc/:partnerSlug/:slug" element={<DmcTripPage />} />
          {/* Same trip for travellers from one city: origin preset, own title and FAQ (SEO) */}
          <Route path="/dmc/:partnerSlug/:slug/from/:city" element={<DmcTripPage />} />

          {/* RouteChef admin: Google login on FEATURES.ADMIN_EMAILS (the partner hub checks it again) */}
          <Route path="/admin" element={<AdminPage />} />

          {/* TAAI convention landing page (QR on the T-shirt points here) */}
          <Route path="/taai" element={<TaaiLanding />} />
          <Route
  		path="/flights/:slug"
  		element={<FlightSeoPage />}
	  />

	<Route
 		 path="/bus/:slug"
  		element={<BusSeoPage />}
	/>

	<Route
  		path="/compare/:slug"
 		 element={<CompareSeoPage />}
	/>
        </Routes>
        </Suspense>
      </Layout>
    </AuthProvider>
  );
}

export default App;
